import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import MemberProfileModal from './MemberProfileModal';

const { FiMessageCircle, FiPhone, FiVideo, FiMoreHorizontal, FiHeart, FiShare2, FiPlus, FiSmile, FiX, FiUsers, FiInfo, FiSend } = FiIcons;

const GroupDetailView = ({ group, onClose }) => {
  const [activeMember, setActiveMember] = useState(null);
  const [message, setMessage] = useState('');
  const [posts, setPosts] = useState(group.posts);

  const reactions = ['❤️', '🫂', '🙏', '✨', '💪'];

  const handlePost = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    const newPost = {
      id: Date.now(),
      author: "You",
      content: message,
      time: "Just now",
      likes: 0,
      comments: 0,
      reactions: []
    };
    setPosts([newPost, ...posts]);
    setMessage('');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="fixed inset-0 z-[150] bg-brand-light flex flex-col md:flex-row overflow-hidden"
    >
      {/* Sidebar */}
      <div className="w-full md:w-80 bg-white border-r border-brand-dark flex flex-col shrink-0">
        <div className="p-6 border-b border-brand-light flex items-center justify-between">
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted">
            <SafeIcon icon={FiX} />
          </button>
          <span className="text-[10px] font-black uppercase tracking-widest text-accent-gold">Community Lobby</span>
          <button className="p-2 hover:bg-brand-light rounded-full text-text-muted">
            <SafeIcon icon={FiMoreHorizontal} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-grow space-y-8">
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4 flex items-center gap-2">
              <SafeIcon icon={FiUsers} className="text-accent-gold" /> Sanctuary Members ({group.members.length})
            </h4>
            <div className="space-y-4">
              {group.members.map(member => (
                <div 
                  key={member.id} 
                  onClick={() => setActiveMember(member)}
                  className="flex items-center justify-between group cursor-pointer hover:bg-brand-light p-2 -mx-2 rounded-2xl transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img src={member.avatar} className="w-10 h-10 rounded-full border-2 border-brand-light" alt="" />
                      {member.status === 'online' && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                      )}
                    </div>
                    <span className="text-sm font-bold text-accent-navy group-hover:text-accent-gold transition-colors">{member.name}</span>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <SafeIcon icon={FiMessageCircle} className="text-text-muted" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Feed */}
      <div className="flex-grow flex flex-col bg-brand-light/50 overflow-y-auto">
        <div className="max-w-2xl mx-auto w-full px-6 py-12 space-y-8">
          <div className="bg-white rounded-[32px] p-6 shadow-sm border border-brand-dark">
            <form onSubmit={handlePost}>
              <div className="flex gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-accent-gold/10 flex items-center justify-center text-accent-gold font-bold">Y</div>
                <textarea 
                  className="flex-grow bg-brand-light rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-accent-gold/20 resize-none h-24"
                  placeholder="Share a memory or word of comfort..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-brand-light">
                <div className="flex gap-2">
                  <button type="button" className="p-2 text-text-muted hover:text-accent-gold"><SafeIcon icon={FiIcons.FiImage} /></button>
                  <button type="button" className="p-2 text-text-muted hover:text-accent-gold"><SafeIcon icon={FiSmile} /></button>
                </div>
                <button type="submit" className="btn-primary py-2 px-6 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                  Post to Sanctuary <SafeIcon icon={FiSend} />
                </button>
              </div>
            </form>
          </div>

          <AnimatePresence>
            {posts.map(post => (
              <motion.div 
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[40px] shadow-sm border border-brand-dark overflow-hidden"
              >
                <div className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center gap-4 cursor-pointer" onClick={() => {
                      const m = group.members.find(mem => mem.name === post.author);
                      if (m) setActiveMember(m);
                    }}>
                      <div className="w-12 h-12 rounded-2xl bg-brand-light text-accent-navy flex items-center justify-center font-bold">
                        {post.author[0]}
                      </div>
                      <div>
                        <h5 className="font-bold text-accent-navy">{post.author}</h5>
                        <p className="text-[10px] text-text-muted font-black uppercase tracking-widest">{post.time}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="p-2 hover:bg-brand-light rounded-xl text-green-500"><SafeIcon icon={FiPhone} /></button>
                      <button className="p-2 hover:bg-brand-light rounded-xl text-blue-500"><SafeIcon icon={FiVideo} /></button>
                    </div>
                  </div>

                  <p className="text-lg text-accent-navy font-light leading-relaxed italic mb-8">
                    "{post.content}"
                  </p>

                  <div className="flex items-center gap-2 mb-8">
                    {reactions.map(emoji => (
                      <button key={emoji} className="w-10 h-10 rounded-full bg-brand-light border border-brand-dark hover:border-accent-gold hover:scale-110 transition-all text-xl flex items-center justify-center">
                        {emoji}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-4 pt-6 border-t border-brand-light">
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-light text-text-muted font-black uppercase tracking-widest text-[10px] hover:text-accent-gold transition-colors">
                      <SafeIcon icon={FiHeart} /> Support
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-light text-text-muted font-black uppercase tracking-widest text-[10px] hover:text-accent-gold transition-colors">
                      <SafeIcon icon={FiMessageCircle} /> Message
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {activeMember && (
          <MemberProfileModal member={activeMember} onClose={() => setActiveMember(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default GroupDetailView;